/*
 * ⚠️ [amended P5b] THE RESOLUTION MOVED TO `@pams/client-core/config`. THIS IS A FACADE
 *  WITH ONE LINE OF ITS OWN, AND THAT LINE IS THE POINT.
 *
 * `pwa/` needs the same precedence — runtime config first, build-time variable second,
 * the same-origin `/api` proxy last — and getting it subtly different in a second app is
 * how one deployment silently talks to the wrong API. So `resolveApiBaseUrl` is shared.
 *
 * What is NOT shared is `import.meta.env.VITE_API_BASE_URL`. That is a Vite construct with
 * no meaning in a plain TypeScript package: typing it there would make the shared client
 * layer depend on `vite/client` for one property read. Each app supplies its own build-time
 * value instead, which is why `apiBaseUrl()` below still lives here.
 *
 * `runtimeConfig.test.ts` addresses all three of these through this module and is unchanged.
 * ⚠️ [amended AUTO-SAVE] It gained the autosave switch's claims below; the three above are untouched.
 */
import { configuredApiBaseUrl, resolveApiBaseUrl } from '@pams/client-core/config';
import { AUTO_SAVE_CONFIG_KEY, resolveAutoSave } from './autoSaveSetting';

export { DEFAULT_API_BASE_URL, resolveApiBaseUrl, type RuntimeConfig } from '@pams/client-core/config';

export const apiBaseUrl = (): string =>
  resolveApiBaseUrl({
    runtime: configuredApiBaseUrl(),
    buildTime: import.meta.env.VITE_API_BASE_URL as string | undefined,
  });

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [AUTO-SAVE] DOES THE ARRANGEMENT BUILDER SAVE ON ITS OWN? — `ENABLE_AUTO_SAVE`, through
 *  `/runtime-config.js` (`autoSaveSetting.ts` holds the rules). ⚠️ [AUTO-SAVE.1] ONLY WHEN THE PAGE IS
 *  TOLD `true`: not in `.env` is OFF, and so is a page that never received a value — a missing key, a
 *  missing file. The unit harness writes `true` itself (`vitest.setup.ts`), as the browser suite's
 *  preview server is told to (`e2e/playwright.config.ts`).
 *
 *  ⚠️ READ PER CALL, because a test sets `window.__PAMS_CONFIG__` per test — the builder reads it
 *  once per MOUNT, never at module load.
 *
 *  ⚠️ WIDENED HERE RATHER THAN TYPED IN `@pams/client-core`. That package's `RuntimeConfig` types the
 *  global, and Duty Mitra compiles it — Duty Mitra is live, so nothing it builds may change, not
 *  even a type (its sourcemaps carry the source). `Partial<RuntimeConfig>` is an anonymous object
 *  type, so it widens to a record of unknowns with no cast; `resolveAutoSave` decides from there.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const autoSaveEnabled = (): boolean => {
  const config: Readonly<Record<string, unknown>> | undefined =
    typeof window === 'undefined' ? undefined : window.__PAMS_CONFIG__;
  return resolveAutoSave(config?.[AUTO_SAVE_CONFIG_KEY]);
};
